/**
 * Nova Collection JavaScript
 * Handles filtering and display of collection products
 */

class NovaCollection {
  constructor(config) {
    this.collectionHandle = config.collectionHandle;
    this.pageSize = config.pageSize || 250;
    this.showRecentlyAddedFilter = config.showRecentlyAddedFilter || false;
    this.showResultsCount = config.showResultsCount || false;
    this.recentlyAddedDays = config.recentlyAddedDays || 30;
    this.recentlyAddedMaxCards = config.recentlyAddedMaxCards ?? 15;
    this.recentlyAddedTag = config.recentlyAddedTag || "nouveau-variant";
    this.sectionId = config.sectionId;
    this.translations = config.translations || {};
    this.assets = config.assets || {};

    // Pagination config
    this.paginationEnabled = config.pagination?.enabled ?? true;
    this.itemsPerPage = config.pagination?.itemsPerPage || 12;
    this.itemsPerPageMobile = config.pagination?.itemsPerPage || 12; // Keep original for mobile
    this.itemsPerPageDesktop = 15; // 15 items per page for desktop
    this.currentPage = 1;
    this.totalPages = 1;

    this.allProducts = [];
    this.selectedCategories = new Set();
    this.allCategories = new Set();
    this.showMemberDiscountOnly = false;
    this.showRecentlyAddedOnly = false;
    this.debounceTimer = null;
    this.isLoadingFinished = false;

    // Read initial state from URL
    this.readStateFromURL();

    this.init();
  }

  async init() {
    // Set up popstate listener for browser back/forward
    window.addEventListener("popstate", event => {
      if (event.state && event.state.novaCollection) {
        this.restoreStateFromHistory(event.state.novaCollection);
      } else {
        this.readStateFromURL();
      }
    });

    // Re-render pagination on window resize (for responsive behavior)
    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Update itemsPerPage based on screen size
        this.updateItemsPerPage();
        if (this.paginationEnabled && this.totalPages > 1) {
          this.renderPageNumbers();
        }
        // Re-render products to apply new itemsPerPage
        this.renderProducts();
      }, 150);
    });

    // Add timeout safety - if fetch takes more than 30 seconds, finish loading anyway
    const timeoutId = setTimeout(() => {
      console.warn("Fetch products timeout - finishing loading");
      this.finishLoading();
    }, 30000);

    try {
      await this.fetchProducts();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Read filters and pagination state from URL query parameters
  readStateFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // Read page number or "all"
    const pageParam = urlParams.get("page");
    if (pageParam) {
      if (pageParam === "all") {
        // View all mode - disable pagination
        this.paginationEnabled = false;
        this.currentPage = 1;
      } else {
        const pageNum = parseInt(pageParam, 10);
        if (!isNaN(pageNum) && pageNum > 0) {
          this.currentPage = pageNum;
          this.paginationEnabled = true;
        }
      }
    }

    // Read member discount filter
    const memberDiscountParam = urlParams.get("member_discount");
    if (memberDiscountParam === "true") {
      this.showMemberDiscountOnly = true;
    }

    // Read recently added filter
    const recentlyAddedParam = urlParams.get("recently_added");
    if (recentlyAddedParam === "true") {
      this.showRecentlyAddedOnly = true;
    }

    // Read categories filter
    const categoriesParam = urlParams.get("categories");
    if (categoriesParam) {
      this.selectedCategories = new Set(categoriesParam.split(",").filter(c => c));
    }
  }

  // Update URL with current state
  updateURL(replaceState = false) {
    const params = new URLSearchParams();

    // Add page parameter
    if (!this.paginationEnabled) {
      // View all mode
      params.set("page", "all");
    } else if (this.currentPage > 1) {
      // Specific page (only if not page 1)
      params.set("page", this.currentPage.toString());
    }
    // If page 1 and pagination enabled, don't add page param (cleaner URLs)

    // Add member discount filter if active
    if (this.showMemberDiscountOnly) {
      params.set("member_discount", "true");
    }

    // Add recently added filter if active
    if (this.showRecentlyAddedOnly) {
      params.set("recently_added", "true");
    }

    // Add categories filter if any selected
    if (this.selectedCategories.size > 0) {
      params.set("categories", Array.from(this.selectedCategories).join(","));
    }

    // Build new URL
    const newURL = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;

    // Create state object
    const state = {
      novaCollection: {
        page: this.paginationEnabled ? this.currentPage : "all",
        memberDiscount: this.showMemberDiscountOnly,
        recentlyAdded: this.showRecentlyAddedOnly,
        categories: Array.from(this.selectedCategories),
        paginationEnabled: this.paginationEnabled,
      },
    };

    // Update browser history
    if (replaceState) {
      window.history.replaceState(state, "", newURL);
    } else {
      window.history.pushState(state, "", newURL);
    }
  }

  // Restore state from history (browser back/forward)
  restoreStateFromHistory(state) {
    // Restore pagination state
    if (state.page === "all") {
      this.paginationEnabled = false;
      this.currentPage = 1;
    } else {
      this.currentPage = state.page || 1;
      this.paginationEnabled = state.paginationEnabled ?? true;
    }

    this.showMemberDiscountOnly = state.memberDiscount || false;
    this.showRecentlyAddedOnly = state.recentlyAdded || false;
    this.selectedCategories = new Set(state.categories || []);

    // Sync UI elements
    this.syncFiltersUI();

    // Re-render
    this.updateResultsCount();
    this.renderProducts();
  }

  // Sync filter UI elements with current state
  syncFiltersUI() {
    // Sync category checkboxes
    document
      .querySelectorAll(
        '#category-checkboxes input[type="checkbox"], #mobile-category-checkboxes input[type="checkbox"]',
      )
      .forEach(checkbox => {
        checkbox.checked = this.selectedCategories.has(checkbox.value);
      });

    // Sync member discount checkboxes
    document.querySelectorAll("#member-discount-checkbox, #mobile-member-discount-checkbox").forEach(checkbox => {
      checkbox.checked = this.showMemberDiscountOnly;
    });

    // Sync recently added checkboxes
    document.querySelectorAll("#recently-added-checkbox, #mobile-recently-added-checkbox").forEach(checkbox => {
      checkbox.checked = this.showRecentlyAddedOnly;
    });
  }

  async fetchProducts(cursor = null) {
    try {
      // Check if collectionHandle is defined
      if (!this.collectionHandle) {
        console.error("Collection handle is not defined", this.collectionHandle);
        this.finishLoading();
        return;
      }

      const query = `
        query {
          collection(handle: "${this.collectionHandle}") {
            products(first: ${this.pageSize}${cursor ? `, after: "${cursor}"` : ""}) {
              edges {
                node {
                  id
                  title
                  handle
                  productType
                  createdAt
                  tags
                  metafields(identifiers: [
                    {namespace: "custom", key: "member_discount"},
                    {namespace: "custom", key: "ovation_url_global"},
                    {namespace: "custom", key: "subtitle"},
                    {namespace: "custom", key: "presale_end_date"},
                    {namespace: "custom", key: "card_image"}
                  ]) {
                    key
                    namespace
                    value
                    reference {
                      ... on MediaImage {
                        image {
                          url
                          altText
                        }
                      }
                    }
                  }
                  featuredImage {
                    url
                    altText
                  }
                  variants(first: 50) {
                    edges {
                      node {
                        id
                        title
                        priceV2 { amount, currencyCode }
                        metafields(identifiers: [
                          {namespace: "custom", key: "date_and_time"},
                          {namespace: "custom", key: "presale_end_date"},
                          {namespace: "custom", key: "is_newly_added"},
                          {namespace: "custom", key: "status"}
                        ]) {
                          key
                          namespace
                          value
                        }
                      }
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      `;

      // Add timeout to fetch request (10 seconds per request)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      let response;
      try {
        response = await fetch("/api/2025-07/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": "c3ff7eddebf3aac91cbf35488d6fb763",
          },
          body: JSON.stringify({query}),
          signal: controller.signal,
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("Request timeout - fetch took too long");
        }
        throw fetchError;
      }
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for GraphQL errors
      if (data.errors) {
        console.error("GraphQL errors:", data.errors);
        this.finishLoading();
        return;
      }

      // Check if collection exists and has products
      if (!data.data || !data.data.collection) {
        console.error("Collection not found or invalid response:", data);
        this.finishLoading();
        return;
      }

      // Handle case where collection exists but has no products
      if (!data.data.collection.products || !data.data.collection.products.edges) {
        console.warn("Collection has no products");
        this.finishLoading();
        return;
      }

      const products = data.data.collection.products.edges.map(edge => edge.node);
      this.allProducts = this.allProducts.concat(products);

      // Collect product types for filtering
      products.forEach(product => {
        if (product.productType) {
          this.allCategories.add(product.productType);
        }
      });

      if (data.data.collection.products.pageInfo.hasNextPage) {
        await this.fetchProducts(data.data.collection.products.pageInfo.endCursor);
      } else {
        // Finished fetching all products
        this.finishLoading();
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      this.finishLoading();
    }
  }

  // Update itemsPerPage based on screen size
  updateItemsPerPage() {
    const isDesktop = window.innerWidth >= 750;
    this.itemsPerPage = isDesktop ? this.itemsPerPageDesktop : this.itemsPerPageMobile;
  }

  // Helper method to finish loading and render products
  finishLoading() {
    // Prevent multiple calls
    if (this.isLoadingFinished) {
      console.warn("finishLoading() called multiple times, ignoring");
      return;
    }
    this.isLoadingFinished = true;

    // Update itemsPerPage based on current screen size
    this.updateItemsPerPage();

    // Always hide loading state
    const loadingContainer = document.getElementById("loading-container");
    if (loadingContainer) {
      loadingContainer.style.display = "none";
    }

    // Always render (even if empty, to show empty state)
    this.renderCategoryFilter();
    this.initializeMemberDiscountFilter();
    if (this.showRecentlyAddedFilter) {
      this.initializeRecentlyAddedFilter();
    }
    this.initializeClearAllFilters();

    // Sync UI with URL state after products are loaded
    this.syncFiltersUI();

    this.updateResultsCount();
    this.renderProducts();

    // Set initial URL state (use replaceState to not add to history)
    this.updateURL(true);
  }

  // Helper to find metafield by key in metafields array
  // Now that GraphQL returns key and namespace, we can find explicitly by key/namespace
  // This is more reliable than guessing by pattern or position
  findMetafieldByKey(metafields, key) {
    if (!metafields || !Array.isArray(metafields)) return null;

    // Find metafield by explicit key and namespace match
    // This matches the Liquid syntax: metafields.custom.presale_end_date
    for (const metafield of metafields) {
      if (!metafield) continue;

      // Check if this metafield matches the key we're looking for
      // GraphQL returns key and namespace, so we can match explicitly
      if (metafield.key === key && metafield.namespace === "custom") {
        // Return the value if it exists (can be null/empty if metafield was deleted)
        return metafield.value || null;
      }
    }

    // If no match found, return null
    return null;
  }

  isVariantAvailable(status) {
    return status !== "Complet" && status !== "À venir";
  }

  resolveCardAvailability(cardVariants, allProductVariants) {
    const tileAvailable = cardVariants.filter(v => this.isVariantAvailable(v.status));
    const productAvailable = allProductVariants.filter(v => this.isVariantAvailable(v.status));

    if (tileAvailable.length > 0) {
      return {
        buttonLabel: this.translations.buy || "Acheter",
        disabled: false,
        secondaryLabel: this.translations.moreInfo || "Plus d'infos",
      };
    }

    if (productAvailable.length > 0) {
      return {
        buttonLabel: this.translations.soldOut || "Complet",
        disabled: true,
        secondaryLabel: this.translations.viewOtherDates || "Voir d'autres dates",
      };
    }

    return {
      buttonLabel: this.translations.soldOut || "Complet",
      disabled: true,
      secondaryLabel: this.translations.moreInfo || "Plus d'infos",
    };
  }

  // Check if all variants in a specific group are in presale
  // This is used for card-level presale checking (each card represents a group of variants)
  // IMPORTANT: This checks only the variants in the provided array, not all variants of a product
  checkVariantsInPresale(variantsArray) {
    try {
      if (!variantsArray || variantsArray.length === 0) {
        return {isPresale: false, presaleEndDate: null};
      }

      const currentDate = new Date();
      let variantsInPresaleCount = 0;
      let earliestPresaleDate = null;
      let earliestPresaleDateObj = null;

      // Check each variant in this group for presale status
      // For ALL variants to be in presale, EVERY variant must have a valid presale_end_date in the future
      for (const variant of variantsArray) {
        if (!variant) {
          // If we can't process a variant, consider it not in presale
          return {isPresale: false, presaleEndDate: null};
        }

        // Get variant-level presale_end_date using explicit lookup
        // IMPORTANT: Each variant must have its own presale_end_date - no fallback to product level
        const variantPresaleEndDate = this.findMetafieldByKey(variant.metafields, "presale_end_date");

        // If this variant has no presale_end_date, this group is NOT in presale
        if (!variantPresaleEndDate) {
          return {isPresale: false, presaleEndDate: null};
        }

        // Check if the date is valid and in the future
        try {
          const variantDate = new Date(variantPresaleEndDate);
          if (isNaN(variantDate.getTime())) {
            // Invalid date, this variant is not in presale
            return {isPresale: false, presaleEndDate: null};
          }

          if (variantDate <= currentDate) {
            // Date is in the past or present, this variant is not in presale
            return {isPresale: false, presaleEndDate: null};
          }

          // This variant is in presale, track it
          variantsInPresaleCount++;
          // Track earliest presale date
          if (!earliestPresaleDateObj || variantDate < earliestPresaleDateObj) {
            earliestPresaleDateObj = variantDate;
            earliestPresaleDate = variantPresaleEndDate;
          }
        } catch (e) {
          // Invalid date format, this variant is not in presale
          return {isPresale: false, presaleEndDate: null};
        }
      }

      // All variants in this group passed the presale check
      const totalVariants = variantsArray.length;
      const allVariantsInPresale = variantsInPresaleCount === totalVariants && totalVariants > 0;

      return {
        isPresale: allVariantsInPresale,
        presaleEndDate: allVariantsInPresale ? earliestPresaleDate : null,
      };
    } catch (error) {
      // If there's any error, return not in presale to prevent breaking the page
      console.error("Error checking presale status for variant group:", error);
      return {isPresale: false, presaleEndDate: null};
    }
  }

  // Check if all variants of a product are in presale
  // IMPORTANT: This function checks ONLY the variants of the given product, not other products
  // Each product is evaluated independently
  // NOTE: This is kept for backward compatibility but may not be used if we check per-card
  checkAllVariantsInPresale(product) {
    try {
      if (!product || !product.variants || !product.variants.edges || product.variants.edges.length === 0) {
        return {isPresale: false, presaleEndDate: null};
      }

      const currentDate = new Date();
      let variantsInPresaleCount = 0;
      let earliestPresaleDate = null;
      let earliestPresaleDateObj = null;

      // Check each variant for presale status
      // For ALL variants to be in presale, EVERY variant must have a valid presale_end_date in the future
      for (const edge of product.variants.edges) {
        if (!edge || !edge.node) {
          // If we can't process a variant, consider it not in presale
          return {isPresale: false, presaleEndDate: null};
        }

        const variant = edge.node;

        // Get variant-level presale_end_date using explicit lookup
        // IMPORTANT: Each variant must have its own presale_end_date - no fallback to product level
        // This ensures that we only consider a product in presale if ALL variants explicitly have presale dates
        const variantPresaleEndDate = this.findMetafieldByKey(variant.metafields, "presale_end_date");

        // If this variant has no presale_end_date, the product is NOT in presale
        // We do NOT fallback to product-level presale_end_date to ensure strict variant-level checking
        if (!variantPresaleEndDate) {
          return {isPresale: false, presaleEndDate: null};
        }

        // Check if the date is valid and in the future
        try {
          const variantDate = new Date(variantPresaleEndDate);
          if (isNaN(variantDate.getTime())) {
            // Invalid date, this variant is not in presale
            return {isPresale: false, presaleEndDate: null};
          }

          if (variantDate <= currentDate) {
            // Date is in the past or present, this variant is not in presale
            return {isPresale: false, presaleEndDate: null};
          }

          // This variant is in presale, track it
          variantsInPresaleCount++;
          // Track earliest presale date
          if (!earliestPresaleDateObj || variantDate < earliestPresaleDateObj) {
            earliestPresaleDateObj = variantDate;
            earliestPresaleDate = variantPresaleEndDate;
          }
        } catch (e) {
          // Invalid date format, this variant is not in presale
          return {isPresale: false, presaleEndDate: null};
        }
      }

      // All variants passed the presale check
      const totalVariants = product.variants.edges.length;
      const allVariantsInPresale = variantsInPresaleCount === totalVariants && totalVariants > 0;

      return {
        isPresale: allVariantsInPresale,
        presaleEndDate: allVariantsInPresale ? earliestPresaleDate : null,
      };
    } catch (error) {
      // If there's any error, return not in presale to prevent breaking the page
      console.error("Error checking presale status:", error, product);
      return {isPresale: false, presaleEndDate: null};
    }
  }

  // Extract filter logic into single method - eliminates duplication
  getFilteredProducts() {
    let filteredProducts = this.allProducts;

    // Apply category filter
    if (this.selectedCategories.size > 0) {
      filteredProducts = filteredProducts.filter(
        product => product.productType && this.selectedCategories.has(product.productType),
      );
    }

    // Apply member discount filter
    if (this.showMemberDiscountOnly) {
      filteredProducts = filteredProducts.filter(product => {
        const memberDiscountValue = this.findMetafieldByKey(product.metafields, "member_discount");
        return memberDiscountValue === "true";
      });
    }

    // Apply recently added filter
    if (this.showRecentlyAddedOnly) {
      filteredProducts = filteredProducts.filter(product => {
        // Only show products with the "nouveau-variant" tag
        if (!product.tags || !Array.isArray(product.tags)) return false;
        const hasNouveauVariantTag = product.tags.some(tag => {
          const tagLower = tag.toLowerCase();
          return tagLower === "nouveau-variant" || tagLower.includes("nouveau-variant");
        });
        return hasNouveauVariantTag;
      });
    }

    return filteredProducts;
  }

  // Debounce filter updates to improve performance
  debounceFilterUpdate() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.currentPage = 1; // Reset to first page when filters change
      this.updateResultsCount();
      this.renderProducts();
      this.updateURL(); // Update URL with new filter state
    }, 150);
  }

  renderCategoryFilter() {
    const desktopContainer = document.getElementById("category-checkboxes");
    const mobileContainer = document.getElementById("mobile-category-checkboxes");
    const desktopFilter = document.getElementById("desktop-category-filter");
    const mobileFilter = document.getElementById("mobile-category-filter");

    // Only show category filter if there are multiple product types
    const shouldShowCategoryFilter = this.allCategories.size > 1;

    if (desktopFilter) {
      desktopFilter.style.display = shouldShowCategoryFilter ? "flex" : "none";
    }
    if (mobileFilter) {
      mobileFilter.style.display = shouldShowCategoryFilter ? "block" : "none";
    }

    // Only render category options if there are multiple types
    if (!shouldShowCategoryFilter) {
      return;
    }

    [desktopContainer, mobileContainer].forEach(container => {
      if (!container) return;
      container.innerHTML = "";

      // Add individual category options
      Array.from(this.allCategories)
        .sort()
        .forEach(category => {
          const label = document.createElement("label");
          label.className = "category-option";
          const checkbox = document.createElement("input");
          checkbox.type = "checkbox";
          checkbox.value = category;
          checkbox.addEventListener("change", () => {
            if (checkbox.checked) {
              this.selectedCategories.add(category);
            } else {
              this.selectedCategories.delete(category);
            }

            // Sync the other container's checkbox
            this.syncCategoryCheckboxes(category, checkbox.checked);

            this.debounceFilterUpdate();
          });

          // Add checkmark element like other filters
          const checkmark = document.createElement("span");
          checkmark.className = "checkmark";

          label.appendChild(checkbox);
          label.appendChild(checkmark);
          label.appendChild(document.createTextNode(` ${category}`));
          container.appendChild(label);
        });
    });
  }

  syncCategoryCheckboxes(category, checked) {
    // Find the other container (desktop if we're in mobile, mobile if we're in desktop)
    const desktopContainer = document.getElementById("category-checkboxes");
    const mobileContainer = document.getElementById("mobile-category-checkboxes");

    // Find the checkbox in the other container and sync it
    [desktopContainer, mobileContainer].forEach(container => {
      if (!container) return;

      const checkbox = container.querySelector(`input[value="${category}"]`);
      if (checkbox && checkbox.checked !== checked) {
        checkbox.checked = checked;
      }
    });
  }

  initializeMemberDiscountFilter() {
    const desktopCheckbox = document.getElementById("member-discount-checkbox");
    const mobileCheckbox = document.getElementById("mobile-member-discount-checkbox");

    [desktopCheckbox, mobileCheckbox].forEach(checkbox => {
      if (!checkbox) return;
      checkbox.addEventListener("change", () => {
        this.showMemberDiscountOnly = checkbox.checked;
        // Sync both checkboxes
        if (desktopCheckbox) desktopCheckbox.checked = checkbox.checked;
        if (mobileCheckbox) mobileCheckbox.checked = checkbox.checked;
        this.debounceFilterUpdate();
      });
    });
  }

  initializeRecentlyAddedFilter() {
    const desktopCheckbox = document.getElementById("recently-added-checkbox");
    const mobileCheckbox = document.getElementById("mobile-recently-added-checkbox");

    [desktopCheckbox, mobileCheckbox].forEach(checkbox => {
      if (!checkbox) return;
      checkbox.addEventListener("change", () => {
        this.showRecentlyAddedOnly = checkbox.checked;
        // Sync both checkboxes
        if (desktopCheckbox) desktopCheckbox.checked = checkbox.checked;
        if (mobileCheckbox) mobileCheckbox.checked = checkbox.checked;
        this.debounceFilterUpdate();
      });
    });
  }

  initializeClearAllFilters() {
    const clearAllButton = document.getElementById("clear-all-filters");
    const clearFiltersNoResults = document.getElementById("clear-filters-no-results");

    const clearAllFilters = () => {
      // Clear category filters
      this.selectedCategories.clear();
      document
        .querySelectorAll(
          '#category-checkboxes input[type="checkbox"], #mobile-category-checkboxes input[type="checkbox"]',
        )
        .forEach(cb => {
          cb.checked = false;
        });

      // Clear member discount filter
      this.showMemberDiscountOnly = false;
      document
        .querySelectorAll("#member-discount-checkbox, #mobile-member-discount-checkbox")
        .forEach(cb => (cb.checked = false));

      // Clear recently added filter
      this.showRecentlyAddedOnly = false;
      document
        .querySelectorAll("#recently-added-checkbox, #mobile-recently-added-checkbox")
        .forEach(cb => (cb.checked = false));

      // Reset to page 1
      this.currentPage = 1;

      this.updateResultsCount();
      this.renderProducts();
      this.updateURL(); // Update URL when clearing filters
    };

    if (clearAllButton) {
      clearAllButton.addEventListener("click", clearAllFilters);
    }

    if (clearFiltersNoResults) {
      clearFiltersNoResults.addEventListener("click", clearAllFilters);
    }
  }

  openMobileFilters() {
    const modal = document.querySelector(`#FacetFiltersForm--${this.sectionId}-mobile dialog`);
    if (modal) {
      modal.showModal();
    }
  }

  closeMobileFilters() {
    const modal = document.querySelector(`#FacetFiltersForm--${this.sectionId}-mobile dialog`);
    if (modal) {
      modal.close();
    }
  }

  updateResultsCount() {
    if (!this.showResultsCount) return;
    const count = this.getFilteredProducts().length;

    // Update desktop results count
    const eventText = count > 1 ? this.translations.events || "événements" : this.translations.event || "événement";
    const resultsCountElement = document.getElementById("results-count");
    if (resultsCountElement) {
      resultsCountElement.textContent = `${count} ${eventText}`;
    }

    // Update mobile results count
    const mobileResultsCountElement = document.getElementById("mobile-results-count");
    if (mobileResultsCountElement) {
      mobileResultsCountElement.textContent = count;
    }

    // Update mobile results text with proper pluralization
    const mobileResultsTextElement = document.getElementById("mobile-results-text");
    if (mobileResultsTextElement) {
      mobileResultsTextElement.textContent = eventText;
    }
  }

  renderProducts() {
    // Update itemsPerPage based on current screen size
    this.updateItemsPerPage();

    // Hide loading state
    const loadingContainer = document.getElementById("loading-container");
    if (loadingContainer) {
      loadingContainer.style.display = "none";
    }

    // Get filtered products using the extracted method
    const filteredProducts = this.getFilteredProducts();

    // Store filtered products for pagination
    this.filteredProducts = filteredProducts;

    // Group variants per product by date proximity (same day, previous day, next day)
    const DAY_MS = 24 * 60 * 60 * 1000;

    // Extract YYYY-MM-DD components directly from the string to avoid timezone shifts.
    // Shopify date-time metafields store the wall-clock time entered in the admin (in the
    // store's timezone). Using Date.parse + getUTCDate would shift evening dates forward
    // by one day for users in a negative-offset timezone (e.g. a 30 mai 20:00 show would
    // become 31 mai), which broke the chronological ordering of cards.
    const toDayKey = dateStr => {
      if (typeof dateStr !== "string") return Number.POSITIVE_INFINITY;
      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return Number.POSITIVE_INFINITY;
      return Date.UTC(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
    };

    // Full timestamp (date + time) used for sorting so that same-day shows are ordered by
    // hour. We keep dayKey for proximity-based grouping only.
    const toTimeKey = dateStr => {
      if (typeof dateStr !== "string") return Number.POSITIVE_INFINITY;
      const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (isoMatch) {
        return Date.UTC(
          parseInt(isoMatch[1], 10),
          parseInt(isoMatch[2], 10) - 1,
          parseInt(isoMatch[3], 10),
          parseInt(isoMatch[4], 10),
          parseInt(isoMatch[5], 10),
          parseInt(isoMatch[6], 10),
        );
      }
      const dateOnlyMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateOnlyMatch) {
        return Date.UTC(
          parseInt(dateOnlyMatch[1], 10),
          parseInt(dateOnlyMatch[2], 10) - 1,
          parseInt(dateOnlyMatch[3], 10),
        );
      }
      return Number.POSITIVE_INFINITY;
    };

    // Map productId -> list of variant records with computed day keys
    // NOTE: We no longer check presale at product level - each card (group of variants) will be checked individually
    const productIdToVariants = new Map();
    filteredProducts.forEach(product => {
      const productId = product.id;
      const list = productIdToVariants.get(productId) || [];

      // Check if product has "nouveau-variant" tag and filter is active
      const hasNouveauVariantTag =
        product.tags &&
        Array.isArray(product.tags) &&
        product.tags.some(tag => {
          const tagLower = tag.toLowerCase();
          return tagLower === "nouveau-variant" || tagLower.includes("nouveau-variant");
        });

      const shouldFilterVariants = this.showRecentlyAddedOnly && hasNouveauVariantTag;
      const variantCutoffDate = shouldFilterVariants ? new Date() : null;
      if (variantCutoffDate) {
        variantCutoffDate.setDate(variantCutoffDate.getDate() - this.recentlyAddedDays);
      }

      product.variants.edges.forEach(edge => {
        const node = edge.node;

        // If product has "nouveau-variant" tag and filter is active, only show recently added variants
        if (shouldFilterVariants) {
          // Check if variant has the is_newly_added metafield
          const isNewlyAddedValue = this.findMetafieldByKey(node.metafields, "is_newly_added");
          if (!isNewlyAddedValue) {
            return; // Skip variants without is_newly_added metafield
          }
          // Check if the date is within the cutoff period
          try {
            const variantAddedDate = new Date(isNewlyAddedValue);
            if (isNaN(variantAddedDate.getTime()) || variantAddedDate < variantCutoffDate) {
              return; // Skip variants with invalid dates or older than the cutoff date
            }
          } catch (e) {
            return; // Skip variants with invalid date format
          }
        }

        const dateField = this.findMetafieldByKey(node.metafields, "date_and_time");
        const newlyAddedRaw = this.findMetafieldByKey(node.metafields, "is_newly_added");
        const newlyAddedKey = (() => {
          if (!newlyAddedRaw) return 0;
          const t = Date.parse(typeof newlyAddedRaw === "string" ? newlyAddedRaw : String(newlyAddedRaw));
          return Number.isNaN(t) ? 0 : t;
        })();

        // Use helper function to find metafields explicitly
        const memberDiscountValue = this.findMetafieldByKey(product.metafields, "member_discount");
        const ovationUrl = this.findMetafieldByKey(product.metafields, "ovation_url_global");
        const subtitle = this.findMetafieldByKey(product.metafields, "subtitle");

        // Get card_image metafield (file_reference type)
        const cardImageMetafieldObj = product.metafields?.find(
          mf => mf && mf.key === "card_image" && mf.namespace === "custom",
        );

        // Get card image: use metafield card_image if available, otherwise use featuredImage
        let cardImage = null;
        if (cardImageMetafieldObj) {
          // Check if we have a reference (from GraphQL query)
          if (cardImageMetafieldObj.reference?.image) {
            const imageRef = cardImageMetafieldObj.reference.image;
            cardImage = {
              url: imageRef.url,
              altText: imageRef.altText || product.featuredImage?.altText || product.title,
            };
          } else if (cardImageMetafieldObj.value) {
            // Fallback: try to parse value if reference is not available
            try {
              const parsedValue =
                typeof cardImageMetafieldObj.value === "string"
                  ? JSON.parse(cardImageMetafieldObj.value)
                  : cardImageMetafieldObj.value;

              if (parsedValue && typeof parsedValue === "object") {
                if (parsedValue.url || parsedValue.src) {
                  cardImage = {
                    url: parsedValue.url || parsedValue.src,
                    altText: parsedValue.altText || product.featuredImage?.altText || product.title,
                  };
                }
              } else if (typeof parsedValue === "string" && parsedValue.startsWith("http")) {
                // Direct URL string
                cardImage = {
                  url: parsedValue,
                  altText: product.featuredImage?.altText || product.title,
                };
              }
            } catch (e) {
              // If parsing fails, ignore and use featuredImage
            }
          }
        }

        // Fallback to featuredImage if card_image is not available
        const displayImage = cardImage || product.featuredImage;

        list.push({
          ...node,
          productTitle: product.title,
          productId,
          dateField,
          dayKey: toDayKey(dateField),
          timeKey: toTimeKey(dateField),
          newlyAddedKey,
          productType: product.productType,
          hasMemberDiscount: memberDiscountValue === "true",
          productHandle: product.handle,
          ovationUrl: ovationUrl || null,
          featuredImage: displayImage, // Use card_image if available, otherwise featuredImage
          subtitle: subtitle || null,
          status: this.findMetafieldByKey(node.metafields, "status"),
          // isPresale and presaleEndDate will be set per-card (per group of variants) later
        });
      });
      productIdToVariants.set(productId, list);
    });

    // Build grouped cards per product
    const cards = [];
    productIdToVariants.forEach(variants => {
      // Separate valid/invalid dates. Sort by full timestamp so multiple variants of the
      // same product appear in chronological order (date + hour).
      const valid = variants.filter(v => Number.isFinite(v.dayKey)).sort((a, b) => a.timeKey - b.timeKey);
      const invalid = variants.filter(v => !Number.isFinite(v.dayKey));

      // Group valid variants by proximity of 1 day
      let currentGroup = [];
      let lastDayKey = null;
      valid.forEach(v => {
        if (currentGroup.length === 0) {
          currentGroup.push(v);
          lastDayKey = v.dayKey;
          return;
        }
        const diff = Math.abs(v.dayKey - lastDayKey);
        if (diff <= DAY_MS) {
          currentGroup.push(v);
          lastDayKey = v.dayKey;
        } else {
          // Check presale status for this group of variants before creating the card
          const presaleInfo = this.checkVariantsInPresale(currentGroup);
          cards.push({
            variants: currentGroup.slice(),
            sortKey: currentGroup[0].timeKey,
            sortKeyNewlyAdded: Math.max(...currentGroup.map(v => v.newlyAddedKey || 0)),
            isPresale: presaleInfo.isPresale,
            presaleEndDate: presaleInfo.presaleEndDate,
          });
          currentGroup = [v];
          lastDayKey = v.dayKey;
        }
      });
      if (currentGroup.length > 0) {
        const presaleInfo = this.checkVariantsInPresale(currentGroup);
        cards.push({
          variants: currentGroup.slice(),
          sortKey: currentGroup[0].timeKey,
          sortKeyNewlyAdded: Math.max(...currentGroup.map(v => v.newlyAddedKey || 0)),
          isPresale: presaleInfo.isPresale,
          presaleEndDate: presaleInfo.presaleEndDate,
        });
      }

      invalid.forEach(v => {
        const presaleInfo = this.checkVariantsInPresale([v]);
        cards.push({
          variants: [v],
          sortKey: Number.POSITIVE_INFINITY,
          sortKeyNewlyAdded: v.newlyAddedKey || 0,
          isPresale: presaleInfo.isPresale,
          presaleEndDate: presaleInfo.presaleEndDate,
        });
      });
    });

    let sortedCards;
    if (this.showRecentlyAddedOnly) {
      const maxCards = Math.max(1, this.recentlyAddedMaxCards);
      sortedCards = cards
        .slice()
        .sort((a, b) => (b.sortKeyNewlyAdded ?? 0) - (a.sortKeyNewlyAdded ?? 0))
        .slice(0, maxCards);
    } else {
      sortedCards = cards.slice().sort((a, b) => a.sortKey - b.sortKey);
    }

    // Calculate pagination
    this.totalPages = this.paginationEnabled ? Math.max(1, Math.ceil(sortedCards.length / this.itemsPerPage)) : 1;

    // Ensure current page is within valid range
    this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));

    // Get cards for current page
    const startIndex = this.paginationEnabled ? (this.currentPage - 1) * this.itemsPerPage : 0;
    const endIndex = this.paginationEnabled ? startIndex + this.itemsPerPage : sortedCards.length;
    const cardsToDisplay = sortedCards.slice(startIndex, endIndex);

    // Show/hide no results view
    const productsContainer = document.getElementById("products-container");
    const noResultsContainer = document.getElementById("no-results-container");

    if (sortedCards.length === 0) {
      if (productsContainer) productsContainer.style.display = "none";
      if (noResultsContainer) noResultsContainer.style.display = "flex";
      this.hidePagination();
    } else {
      if (productsContainer) {
        productsContainer.style.display = "grid";
        productsContainer.innerHTML = "";
      }
      if (noResultsContainer) noResultsContainer.style.display = "none";

      // Render one card per group of variants (only for current page)
      cardsToDisplay.forEach((card, cardIndex) => {
        const cardDiv = document.createElement("div");
        cardDiv.classList.add("event-card");

        // Get product info from first variant
        const firstVariant = card.variants[0];
        const productTitle = firstVariant.productTitle;
        const productType = firstVariant.productType;
        const hasMemberDiscount = firstVariant.hasMemberDiscount;
        const productHandle = firstVariant.productHandle;
        const ovationUrl = firstVariant.ovationUrl;
        const featuredImage = firstVariant.featuredImage;
        const subtitle = firstVariant.subtitle;
        // Get presale info from the card (checked per card, not per product)
        const isPresale = card.isPresale || false;
        const presaleEndDate = card.presaleEndDate || null;

        // Generate unique button ID for this card using page and index
        const buttonId = `nova-collection-buy-${productHandle}-${this.currentPage}-${cardIndex}`;

        // Format date for display - show range for grouped variants
        const formatDate = dateStr => {
          if (!dateStr) return this.translations.dateToConfirm || "Date à confirmer";
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return dateStr;

          const options = {
            day: "numeric",
            month: "long",
            year: "numeric",
          };
          return date.toLocaleDateString("fr-FR", options);
        };

        // Helper function to format day without leading zero
        const formatDay = date => {
          return date.getDate().toString();
        };

        // Helper function to format month and year
        const formatMonthYear = date => {
          return date.toLocaleDateString("fr-FR", {month: "long", year: "numeric"});
        };

        // Get all valid dates from the group
        const validDates = card.variants
          .map(v => v.dateField)
          .filter(dateStr => dateStr)
          .map(dateStr => new Date(dateStr))
          .filter(date => !isNaN(date.getTime()))
          .sort((a, b) => a - b);

        let displayDate;
        if (validDates.length === 0) {
          displayDate = this.translations.dateToConfirm || "Date à confirmer";
        } else if (validDates.length === 1) {
          displayDate = formatDate(validDates[0]);
        } else {
          // Show date range for grouped variants
          const firstDate = validDates[0];
          const lastDate = validDates[validDates.length - 1];

          // Check if dates are on the same day
          const sameDay = firstDate.toDateString() === lastDate.toDateString();

          if (sameDay) {
            displayDate = formatDate(firstDate);
          } else {
            // Pre-calculate common values to avoid repetition
            const firstDay = formatDay(firstDate);
            const lastDay = formatDay(lastDate);
            const toText = this.translations.to || "au";

            // Check if dates are consecutive (difference of 1 day)
            const timeDiff = lastDate.getTime() - firstDate.getTime();
            const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            const isConsecutive = daysDiff === 1;

            // Check if dates are in the same month and year
            const sameMonth = firstDate.getMonth() === lastDate.getMonth();
            const sameYear = firstDate.getFullYear() === lastDate.getFullYear();
            const sameMonthYear = sameMonth && sameYear;

            if (isConsecutive && sameMonthYear) {
              // Format: "7 et 8 décembre 2025"
              const monthYear = formatMonthYear(firstDate);
              displayDate = `${firstDay} et ${lastDay} ${monthYear}`;
            } else if (sameMonthYear) {
              // Same month but not consecutive: "7 au 15 décembre 2025"
              const monthYear = formatMonthYear(firstDate);
              displayDate = `${firstDay} ${toText} ${lastDay} ${monthYear}`;
            } else {
              // Different months: "7 décembre au 8 janvier 2025"
              const firstMonthYear = formatMonthYear(firstDate);
              const lastMonthYear = formatMonthYear(lastDate);
              displayDate = `${firstDay} ${firstMonthYear} ${toText} ${lastDay} ${lastMonthYear}`;
            }
          }
        }

        // Build card HTML safely
        const imageHtml =
          featuredImage && featuredImage.url
            ? `<img src="${featuredImage.url}" alt="${featuredImage.altText || productTitle}" class="card-image-img">`
            : `<div class="placeholder-image"></div>`;

        const memberDiscountBadge = hasMemberDiscount
          ? `<span class="nova-card-badge member-discount-tag">${this.translations.memberDiscount || "Rabais membre"}</span>`
          : "";

        const subtitleHtml = subtitle ? `<div class="card-subtitle">${subtitle}</div>` : "";

        const allProductVariants = productIdToVariants.get(firstVariant.productId) || card.variants;
        const availability = this.resolveCardAvailability(card.variants, allProductVariants);
        const showPresale = isPresale && !availability.disabled;
        const buyButtonHtml = this.renderBuyButton(buttonId, ovationUrl, showPresale, presaleEndDate, {
          disabled: availability.disabled,
          label: availability.buttonLabel,
        });

        // Card content
        cardDiv.innerHTML = `
          <a href="/products/${productHandle}" class="card-link">
            <div class="card-image nova-event-type-container">
              ${imageHtml}
              <div class="card-tags nova-event-type-overlay">
                <span class="nova-card-badge category-tag">${productType}</span>
                ${memberDiscountBadge}
              </div>
            </div>
            <div class="card-content">
              <h5 class="card-title">${productTitle}</h5>
              ${subtitleHtml}
              <div class="card-date">${displayDate}</div>
              <div class="card-actions">
                <span class="link">
                  ${this.assets.iconLink || ""}
                  ${availability.secondaryLabel}
                </span>
              </div>
            </div>
          </a>
          ${buyButtonHtml}
        `;

        if (productsContainer) {
          productsContainer.appendChild(cardDiv);

          // Initialize tooltip if in presale
          if (showPresale && ovationUrl && presaleEndDate) {
            this.initializePresaleTooltip(buttonId);
          }
        }
      });

      // Initialize presale tooltips with formatted dates (after all cards are rendered)
      if (typeof window !== "undefined" && window.NovaDateTime && window.NovaDateTime.initPresaleTooltips) {
        window.NovaDateTime.initPresaleTooltips(productsContainer);
      }

      // Render pagination controls
      this.renderPagination();
    }
  }

  // Pagination Methods
  renderPagination() {
    const paginationContainer = document.getElementById("pagination-container");
    if (!paginationContainer || !this.paginationEnabled || this.totalPages <= 1) {
      this.hidePagination();
      return;
    }

    // Show pagination
    paginationContainer.style.display = "flex";

    // Update Previous/Next buttons
    const prevButton = document.getElementById("pagination-previous");
    const nextButton = document.getElementById("pagination-next");

    if (prevButton) {
      prevButton.disabled = this.currentPage === 1;
      prevButton.onclick = () => this.goToPage(this.currentPage - 1);
    }

    if (nextButton) {
      nextButton.disabled = this.currentPage === this.totalPages;
      nextButton.onclick = () => this.goToPage(this.currentPage + 1);
    }

    // Render page numbers
    this.renderPageNumbers();
  }

  renderPageNumbers() {
    const numbersContainer = document.getElementById("pagination-numbers");
    if (!numbersContainer) return;

    numbersContainer.innerHTML = "";

    // Check if mobile
    const isMobile = window.innerWidth < 750;

    // Logic for which page numbers to show
    const pagesToShow = new Set();

    if (isMobile) {
      // Mobile: Only show current page
      pagesToShow.add(this.currentPage);
    } else {
      // Desktop: Show more pages
      // Always show first page
      pagesToShow.add(1);

      // Always show last 3 pages
      if (this.totalPages > 1) {
        pagesToShow.add(this.totalPages);
        if (this.totalPages > 2) pagesToShow.add(this.totalPages - 1);
        if (this.totalPages > 3) pagesToShow.add(this.totalPages - 2);
      }

      // Show current page and neighbors
      if (this.currentPage > 1 && this.currentPage < this.totalPages) {
        pagesToShow.add(this.currentPage);
        if (this.currentPage > 1) pagesToShow.add(this.currentPage - 1);
        if (this.currentPage < this.totalPages) pagesToShow.add(this.currentPage + 1);
      }

      // Also show page 2 if it exists
      if (this.totalPages > 1) {
        pagesToShow.add(2);
      }
    }

    // Convert to sorted array
    const sortedPages = Array.from(pagesToShow).sort((a, b) => a - b);

    // Render page buttons with ellipsis where needed
    sortedPages.forEach((pageNum, index) => {
      // Add ellipsis if there's a gap (desktop only)
      if (!isMobile && index > 0 && pageNum - sortedPages[index - 1] > 1) {
        const ellipsis = document.createElement("span");
        ellipsis.className = "pagination-ellipsis";
        ellipsis.textContent = "...";
        numbersContainer.appendChild(ellipsis);
      }

      // Add page button
      const pageButton = document.createElement("button");
      pageButton.className = "pagination-page-button";
      if (pageNum === this.currentPage) {
        pageButton.classList.add("active");
      }
      pageButton.textContent = pageNum;
      pageButton.onclick = () => this.goToPage(pageNum);
      numbersContainer.appendChild(pageButton);
    });

    // Add "View All" button
    const viewAllButton = document.createElement("button");
    viewAllButton.className = "pagination-view-all";
    viewAllButton.textContent = this.translations.viewAll || "Tout voir";
    viewAllButton.onclick = () => this.viewAll();
    numbersContainer.appendChild(viewAllButton);
  }

  goToPage(pageNum) {
    if (pageNum < 1 || pageNum > this.totalPages) return;
    this.currentPage = pageNum;
    this.renderProducts();
    this.updateURL(); // Update URL when changing pages

    // Scroll to top of products only when user clicked pagination (page > 1), not on every render
    if (this.paginationEnabled && pageNum > 1) {
      requestAnimationFrame(() => {
        const container = document.getElementById("custom-collection-container");
        if (container) {
          container.scrollIntoView({ behavior: "auto", block: "start" });
        }
      });
    }
  }

  viewAll() {
    this.paginationEnabled = false;
    this.currentPage = 1;
    this.renderProducts();
    this.updateURL(); // Update URL when viewing all
  }

  hidePagination() {
    const paginationContainer = document.getElementById("pagination-container");
    if (paginationContainer) {
      paginationContainer.style.display = "none";
    }
  }

  // Format presale date for display
  formatPresaleDate(dateStr) {
    if (!dateStr) return "";
    try {
      // Shopify date-time metafields: The date is stored in the store's timezone (GMT-5)
      // When retrieved via GraphQL, it may come as UTC (with Z) or without timezone
      // The issue: If it comes as "2026-01-15T20:00:00Z", JavaScript interprets as 20:00 UTC
      // which when converted to GMT-5 becomes 15:00, but the user expects 20:00 GMT-5

      // Solution: Extract the time components directly from the string and format them
      // This preserves the original time the user entered, regardless of timezone interpretation
      const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);

      if (isoMatch) {
        const [, year, month, day, hour, minute] = isoMatch;
        const monthNames = [
          "janvier",
          "février",
          "mars",
          "avril",
          "mai",
          "juin",
          "juillet",
          "août",
          "septembre",
          "octobre",
          "novembre",
          "décembre",
        ];
        const monthName = monthNames[parseInt(month) - 1];
        const dayNum = parseInt(day);
        const hourNum = parseInt(hour);
        const minuteNum = parseInt(minute);

        // Format: "15 janvier 2026 à 20:00"
        const formattedHour = hourNum.toString().padStart(2, "0");
        const formattedMinute = minuteNum.toString().padStart(2, "0");

        return `${dayNum} ${monthName} ${year} à ${formattedHour}:${formattedMinute}`;
      }

      // Fallback to original method if format doesn't match
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const options = {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Toronto",
      };
      return date.toLocaleDateString("fr-FR", options);
    } catch (e) {
      return dateStr;
    }
  }

  // Render buy button and tooltip HTML safely
  renderBuyButton(buttonId, ovationUrl, isPresale, presaleEndDate, options = {}) {
    const {disabled = false, label = null} = options;
    const buttonIcon = this.assets.iconTickets || "";
    const buyText = label || this.translations.buy || "Acheter";

    if (!disabled && !ovationUrl) return "";

    const presaleClass = isPresale && !disabled ? "nova-event-button--presale" : "";
    const disabledClass = disabled ? "nova-event-button--disabled" : "";
    const roleAttr = isPresale && !disabled ? 'role="button"' : "";
    const linkAttrs =
      disabled || !ovationUrl
        ? 'role="link" aria-disabled="true"'
        : `href="${ovationUrl}" target="_blank"`;

    let buttonHtml = `<a id="${buttonId}" ${linkAttrs} class="buy-button-overlay button ${presaleClass} ${disabledClass}" ${roleAttr}>`;
    buttonHtml += `<div class="button__icon">${buttonIcon}</div>`;
    buttonHtml += buyText;
    buttonHtml += `</a>`;

    // Add tooltip if in presale
    if (isPresale && presaleEndDate) {
      const presaleMemberText = this.translations.presaleMember || "Prévente membre";
      const iconClose = this.assets.iconClose || "";
      const iconLink = this.assets.iconLink || "";
      const presaleMembershipHtml =
        this.translations.presaleMembershipHtml ||
        "Les membres peuvent acheter dès maintenant. Les billets seront en vente au public le";
      const publicSaleText = this.translations.publicSale || "Vente publique";
      const onDateText = this.translations.onDate || "Le";

      buttonHtml += `<div id="${buttonId}-tooltip" class="button__tooltip" style="display: none;">`;
      buttonHtml += `<div class="tooltip-content">`;
      buttonHtml += `<div class="tooltip-content-header">`;
      buttonHtml += `<div class="tooltip-title"><span>${presaleMemberText}</span></div>`;
      buttonHtml += `<button class="tooltip-close" aria-label="Close">${iconClose}</button>`;
      buttonHtml += `</div>`;
      buttonHtml += `<div>`;
      buttonHtml += `<a href="${ovationUrl}" class="link">${iconLink}<span class="button__label">${buyText}</span></a>`;
      buttonHtml += `</div>`;
      buttonHtml += `<span class="tooltip-text">${presaleMembershipHtml}</span>`;
      buttonHtml += `</div>`;
      buttonHtml += `<div class="tooltip-content">`;
      buttonHtml += `<div class="tooltip-title"><strong>${publicSaleText}</strong></div>`;
      buttonHtml += `<span class="tooltip-text" data-on-date-text="${onDateText}">`;
      buttonHtml += `<span data-presale-end="${presaleEndDate}" data-presale-end-output></span>`;
      buttonHtml += `</span>`;
      buttonHtml += `</div>`;
      buttonHtml += `</div>`;
    }

    return buttonHtml;
  }

  // Initialize tippy.js tooltip for presale button
  initializePresaleTooltip(buttonId, retryCount = 0) {
    const MAX_RETRIES = 20;
    const RETRY_DELAY = 100;

    // Wait for tippy to be available with retry limit
    if (typeof tippy === "undefined") {
      if (retryCount >= MAX_RETRIES) {
        console.warn(
          `Tippy.js not loaded after ${MAX_RETRIES} retries, aborting tooltip initialization for ${buttonId}`,
        );
        return;
      }
      // Retry after a short delay if tippy isn't loaded yet
      setTimeout(() => this.initializePresaleTooltip(buttonId, retryCount + 1), RETRY_DELAY);
      return;
    }

    const button = document.getElementById(buttonId);
    if (!button) {
      return;
    }

    const tooltipContent = document.getElementById(`${buttonId}-tooltip`);
    if (!tooltipContent) {
      return;
    }

    // Initialize presale date formatting before setting up tippy
    if (typeof window !== "undefined" && window.NovaDateTime && window.NovaDateTime.initPresaleTooltips) {
      window.NovaDateTime.initPresaleTooltips(tooltipContent);
    }

    // Prevent default link behavior on click when in presale
    button.addEventListener("click", e => {
      e.preventDefault();
    });

    // Initialize tippy
    try {
      tippy(`#${buttonId}`, {
        content: tooltipContent.innerHTML,
        allowHTML: true,
        interactive: true,
        trigger: "click",
        theme: "nova-event-button",
        onShown(instance) {
          // Add event listener to the close button when tooltip is shown
          const closeBtn = instance.popper.querySelector(".tooltip-close");
          if (closeBtn) {
            closeBtn.addEventListener("click", () => {
              instance.hide();
            });
          }
        },
      });
    } catch (error) {
      console.error(`Error initializing tippy tooltip for ${buttonId}:`, error);
    }
  }
}

// Global functions for mobile filter modal
window.openMobileFilters = function () {
  if (window.novaCollection) {
    window.novaCollection.openMobileFilters();
  }
};

window.closeMobileFilters = function () {
  if (window.novaCollection) {
    window.novaCollection.closeMobileFilters();
  }
};

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  // Configuration will be passed from Liquid template
  if (window.novaCollectionConfig) {
    window.novaCollection = new NovaCollection(window.novaCollectionConfig);
  }
});
